import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function SuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    status: "pending",
    payment_status: null,
    amount_total: null,
    currency: "usd",
    product_id: null,
    error: null,
  });

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, error: "Missing session_id" }));
      return;
    }
    let cancelled = false;
    const poll = async (attempt = 0) => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setState({ ...res.data, error: null });
          return;
        }
        if (res.data.status === "expired") {
          setState({ ...res.data, error: "Session expired" });
          return;
        }
        if (attempt >= MAX_ATTEMPTS) {
          setState({
            ...res.data,
            error: "Still processing. Check your email for confirmation.",
          });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (e) {
        console.error(e);
        setState((s) => ({ ...s, error: "Couldn't verify payment" }));
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const isPaid = state.payment_status === "paid";
  const isPending = !isPaid && !state.error;

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-6 py-24">
      <div
        className="max-w-lg w-full text-center border border-[#E8D7D0] rounded-3xl p-10 bg-white"
        data-testid="success-page"
      >
        <span className="text-[11px] tracking-[0.3em] uppercase text-[#C48B9F]">
          {isPaid ? "Thank you" : isPending ? "Almost there" : "Update"}
        </span>
        <h1 className="mt-4 font-serif-me text-4xl sm:text-5xl text-[#36292A]">
          {isPaid
            ? "Your box is on its way."
            : isPending
              ? "Confirming your payment…"
              : "Payment update"}
        </h1>
        <p className="mt-5 text-[#7D6B6D] leading-relaxed">
          {isPaid && (
            <>
              Thank you for supporting Me Amor. A confirmation email will land
              in your inbox shortly.
            </>
          )}
          {isPending && !state.error && (
            <>We're checking with Stripe. This usually takes a few seconds.</>
          )}
          {state.error && <>{state.error}</>}
        </p>

        {state.amount_total !== null && (
          <div className="mt-8 inline-block bg-[#F9F6F0] rounded-full px-6 py-2 text-sm text-[#36292A]">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: (state.currency || "usd").toUpperCase(),
            }).format(state.amount_total)}
          </div>
        )}

        <div className="mt-10">
          <Link
            to="/"
            className="me-btn-primary"
            data-testid="success-back-home"
          >
            Back to Me Amor
          </Link>
        </div>
      </div>
    </div>
  );
}
